// src/pages/MyAccountPage.jsx
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LocationContext } from '../contexts/LocationContext';
import Header from '../components/layout/Header';
import ApiService from '../services/ApiService';
import '../styles/my-account.css';

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { user, updateUserProfile } = useAuth();
  const locationContext = useContext(LocationContext);
  const { deliveryLocation } = locationContext || {};

  // ── État : modale de suppression de compte (existant) ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading]     = useState(false);
  const [deleteError, setDeleteError]         = useState('');

  // ── État : modale d'édition du profil ──────────────────
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileLoading, setEditProfileLoading]     = useState(false);
  const [editProfileError, setEditProfileError]         = useState('');
  const [editProfileSuccess, setEditProfileSuccess]     = useState(false);
  const [profileForm, setProfileForm] = useState({
    prenom: '',
    nom:    '',
    email:  ''
  });

  // ── État : modale d'édition des préférences ─────────────
  const [showEditPrefsModal, setShowEditPrefsModal] = useState(false);
  const [editPrefsLoading, setEditPrefsLoading]     = useState(false);
  const [editPrefsError, setEditPrefsError]         = useState('');
  const [editPrefsSuccess, setEditPrefsSuccess]     = useState(false);
  const [prefsForm, setPrefsForm] = useState({
    langue:                'fr',
    moyen_paiement_defaut: ''
  });

  // ── Helpers d'affichage ────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponible';
    try {
      const date = new Date(dateString);
      const day   = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year  = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (err) {
      return 'Date invalide';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Non renseigné';
    if (phone.length === 9) {
      return `${phone.substring(0, 3)} ${phone.substring(3, 6)} ${phone.substring(6)}`;
    }
    return phone;
  };

  const getLangueLabel = (code) => {
    const map = { fr: 'Français', en: 'English' };
    return map[code] || 'Français';
  };

  const getPaymentLabel = (code) => {
    const map = {
      orange_money: 'Orange Money',
      mtn_momo:     'MTN MoMo'
    };
    return map[code] || 'Non défini';
  };

  // ══════════════════════════════════════════════════════
  // MODALE — Suppression de compte (existant, non modifié)
  // ══════════════════════════════════════════════════════
  const handleDeleteClick = () => {
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const response = await ApiService.deleteAccount();
      if (response.success) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('cart');
        localStorage.removeItem('deliveryLocation');
        window.location.href = '/';
      } else {
        setDeleteError(response.message || 'Une erreur est survenue. Veuillez réessayer.');
        setDeleteLoading(false);
      }
    } catch (error) {
      setDeleteError('Une erreur est survenue. Veuillez réessayer.');
      setDeleteLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════
  // MODALE — Édition du profil
  // ══════════════════════════════════════════════════════
  const handleOpenEditProfile = () => {
    setProfileForm({
      prenom: user?.prenom || '',
      nom:    user?.nom    || '',
      email:  user?.email  || ''
    });
    setEditProfileError('');
    setEditProfileSuccess(false);
    setShowEditProfileModal(true);
  };

  const handleCloseEditProfile = () => {
    if (editProfileLoading) return;
    setShowEditProfileModal(false);
    setEditProfileError('');
    setEditProfileSuccess(false);
  };

  const handleProfileFormChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setEditProfileLoading(true);
    setEditProfileError('');
    setEditProfileSuccess(false);

    try {
      const response = await ApiService.updateProfile({
        action: 'update-profile',
        prenom: profileForm.prenom,
        nom:    profileForm.nom,
        email:  profileForm.email
      });

      if (response.success) {
        // Mettre à jour le contexte utilisateur
        updateUserProfile(response.user);
        setEditProfileSuccess(true);
        // Fermer la modale après 1.5s
        setTimeout(() => {
          setShowEditProfileModal(false);
          setEditProfileSuccess(false);
        }, 1500);
      } else {
        setEditProfileError(response.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      setEditProfileError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setEditProfileLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════
  // MODALE — Édition des préférences
  // ══════════════════════════════════════════════════════
  const handleOpenEditPrefs = () => {
    setPrefsForm({
      langue:                user?.langue                || 'fr',
      moyen_paiement_defaut: user?.moyen_paiement_defaut || ''
    });
    setEditPrefsError('');
    setEditPrefsSuccess(false);
    setShowEditPrefsModal(true);
  };

  const handleCloseEditPrefs = () => {
    if (editPrefsLoading) return;
    setShowEditPrefsModal(false);
    setEditPrefsError('');
    setEditPrefsSuccess(false);
  };

  const handlePrefsFormChange = (e) => {
    const { name, value } = e.target;
    setPrefsForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSavePrefs = async () => {
    setEditPrefsLoading(true);
    setEditPrefsError('');
    setEditPrefsSuccess(false);

    try {
      const response = await ApiService.updateProfile({
        action:                'update-preferences',
        langue:                prefsForm.langue,
        moyen_paiement_defaut: prefsForm.moyen_paiement_defaut || null
      });

      if (response.success) {
        updateUserProfile(response.user);
        setEditPrefsSuccess(true);
        setTimeout(() => {
          setShowEditPrefsModal(false);
          setEditPrefsSuccess(false);
        }, 1500);
      } else {
        setEditPrefsError(response.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      setEditPrefsError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setEditPrefsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/welcome-page');
  };

  // ══════════════════════════════════════════════════════
  // RENDU
  // ══════════════════════════════════════════════════════
  return (
    <div className="my-account-page">
      <Header
        title="Mon compte"
        showBackButton={true}
        onBackClick={handleBackClick}
      />

      <div className="my-account-container">

        {/* ── Section profil ── */}
        <div className="profile-section">
          <div className="profile-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h2 className="profile-name">{user?.prenom} {user?.nom}</h2>
          <p className="profile-email">{user?.email || 'Email non renseigné'}</p>
        </div>

        {/* ── Informations personnelles ── */}
        <div className="account-section">
          <div className="section-header">
            <h3 className="section-title">Informations personnelles</h3>
            <button className="section-edit-btn" onClick={handleOpenEditProfile}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modifier
            </button>
          </div>

          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Prénom</span>
                <span className="info-value">{user?.prenom || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Nom</span>
                <span className="info-value">{user?.nom || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 6L2 7" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Email</span>
                <span className="info-value">{user?.email || 'Non renseigné'}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Téléphone</span>
                <span className="info-value">{formatPhone(user?.telephone)}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Membre depuis</span>
                <span className="info-value">{formatDate(user?.date_creation)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Adresse de livraison (non modifiable pour l'instant) ── */}
        <div className="account-section">
          <div className="section-header">
            <h3 className="section-title">Adresse de livraison</h3>
          </div>

          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Adresse par défaut</span>
                <span className="info-value">
                  {deliveryLocation?.address || 'Aucune adresse enregistrée'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Préférences ── */}
        <div className="account-section">
          <div className="section-header">
            <h3 className="section-title">Préférences</h3>
            <button className="section-edit-btn" onClick={handleOpenEditPrefs}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modifier
            </button>
          </div>

          <div className="info-group">
            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Langue</span>
                <span className="info-value">{getLangueLabel(user?.langue)}</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <div className="info-content">
                <span className="info-label">Moyen de paiement par défaut</span>
                <span className="info-value">{getPaymentLabel(user?.moyen_paiement_defaut)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bouton supprimer le compte ── */}
        <div className="delete-account-section">
          <button className="delete-account-btn" onClick={handleDeleteClick}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
            Supprimer mon compte
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          MODALE — Suppression de compte (existant)
      ══════════════════════════════════════════════════ */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={handleCancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="delete-modal-title">Supprimer mon compte</h3>
            <p className="delete-modal-message">Êtes-vous sûr de vouloir supprimer votre compte ?</p>
            <p className="delete-modal-warning">
              Cette action est irréversible. Vous ne pourrez plus accéder à vos commandes et votre historique sera perdu.
            </p>
            {deleteError && <p className="delete-modal-error">{deleteError}</p>}
            <div className="delete-modal-actions">
              <button className="delete-modal-cancel" onClick={handleCancelDelete} disabled={deleteLoading}>
                Annuler
              </button>
              <button className="delete-modal-confirm" onClick={handleConfirmDelete} disabled={deleteLoading}>
                {deleteLoading ? (
                  <><span className="delete-modal-spinner" />Suppression...</>
                ) : (
                  'Confirmer la suppression'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MODALE — Édition des informations personnelles
      ══════════════════════════════════════════════════ */}
      {showEditProfileModal && (
        <div className="edit-modal-overlay" onClick={handleCloseEditProfile}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>

            <div className="edit-modal-header">
              <h3 className="edit-modal-title">Informations personnelles</h3>
              <button className="edit-modal-close" onClick={handleCloseEditProfile} disabled={editProfileLoading}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="edit-modal-body">
              <div className="edit-modal-field">
                <label className="edit-modal-label">Prénom</label>
                <input
                  type="text"
                  name="prenom"
                  className="edit-modal-input"
                  value={profileForm.prenom}
                  onChange={handleProfileFormChange}
                  placeholder="Votre prénom"
                  disabled={editProfileLoading}
                />
              </div>

              <div className="edit-modal-field">
                <label className="edit-modal-label">Nom <span className="edit-modal-required">*</span></label>
                <input
                  type="text"
                  name="nom"
                  className="edit-modal-input"
                  value={profileForm.nom}
                  onChange={handleProfileFormChange}
                  placeholder="Votre nom"
                  disabled={editProfileLoading}
                />
              </div>

              <div className="edit-modal-field">
                <label className="edit-modal-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="edit-modal-input"
                  value={profileForm.email}
                  onChange={handleProfileFormChange}
                  placeholder="votre@email.com"
                  disabled={editProfileLoading}
                />
              </div>
            </div>

            {editProfileError && (
              <p className="edit-modal-error">{editProfileError}</p>
            )}

            {editProfileSuccess && (
              <div className="edit-modal-success">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Modifications enregistrées !
              </div>
            )}

            <div className="edit-modal-actions">
              <button className="edit-modal-cancel" onClick={handleCloseEditProfile} disabled={editProfileLoading}>
                Annuler
              </button>
              <button className="edit-modal-save" onClick={handleSaveProfile} disabled={editProfileLoading || editProfileSuccess}>
                {editProfileLoading ? (
                  <><span className="edit-modal-spinner" />Enregistrement...</>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MODALE — Édition des préférences
      ══════════════════════════════════════════════════ */}
      {showEditPrefsModal && (
        <div className="edit-modal-overlay" onClick={handleCloseEditPrefs}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>

            <div className="edit-modal-header">
              <h3 className="edit-modal-title">Préférences</h3>
              <button className="edit-modal-close" onClick={handleCloseEditPrefs} disabled={editPrefsLoading}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="edit-modal-body">
              <div className="edit-modal-field">
                <label className="edit-modal-label">Langue</label>
                <select
                  name="langue"
                  className="edit-modal-select"
                  value={prefsForm.langue}
                  onChange={handlePrefsFormChange}
                  disabled={editPrefsLoading}
                >
                  <option value="fr">Français</option>
                </select>
              </div>

              <div className="edit-modal-field">
                <label className="edit-modal-label">Moyen de paiement par défaut</label>
                <select
                  name="moyen_paiement_defaut"
                  className="edit-modal-select"
                  value={prefsForm.moyen_paiement_defaut}
                  onChange={handlePrefsFormChange}
                  disabled={editPrefsLoading}
                >
                  <option value="">Non défini</option>
                  <option value="orange_money">Orange Money</option>
                  <option value="mtn_momo">MTN MoMo</option>
                </select>
              </div>
            </div>

            {editPrefsError && (
              <p className="edit-modal-error">{editPrefsError}</p>
            )}

            {editPrefsSuccess && (
              <div className="edit-modal-success">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Préférences enregistrées !
              </div>
            )}

            <div className="edit-modal-actions">
              <button className="edit-modal-cancel" onClick={handleCloseEditPrefs} disabled={editPrefsLoading}>
                Annuler
              </button>
              <button className="edit-modal-save" onClick={handleSavePrefs} disabled={editPrefsLoading || editPrefsSuccess}>
                {editPrefsLoading ? (
                  <><span className="edit-modal-spinner" />Enregistrement...</>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccountPage;